'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  batchArchiveMediaGalleryItemsService,
  createMediaGalleryItemService,
} from '@/services/httpServices/MediaGalleryServices';
import type { HttpServiceConfig } from '@/services/httpServices/types';
import { trackUpload } from '@/tracking/gallery';
import { addUpload, updateUpload } from '@/utils/uploadStateManager';

import { mediaGalleryItemKeys, mediaGalleryListKeys } from '../queries/mediaGalleryQueryKeys';
import { useMediaGalleryItemQuery } from '../queries/useMediaGalleryQuery';
import { useQueryConfig } from '../queries/useQueryConfig';
import {
  useFirebaseStorageUrlMutation,
  useFirebaseUploadMutation,
} from './useUploadMutation';

/**
 * Hook for creating a media gallery item
 */
export function useCreateMediaGalleryItemMutation(httpRequestConfigProp?: HttpServiceConfig) {
  const queryClient = useQueryClient();
  const { httpRequestConfig: configFromStore } = useQueryConfig();
  const config = httpRequestConfigProp ?? configFromStore;

  return useMutation({
    mutationFn: createMediaGalleryItemService,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: mediaGalleryListKeys({ httpRequestConfig: config }),
      });
    },
  });
}

/**
 * Hook for batch archiving multiple media gallery items
 */
export function useArchiveMediaGalleryItemsBatch(httpRequestConfigProp?: HttpServiceConfig) {
  const queryClient = useQueryClient();
  const { httpRequestConfig: configFromStore } = useQueryConfig();
  const config = httpRequestConfigProp ?? configFromStore;

  return useMutation({
    mutationFn: batchArchiveMediaGalleryItemsService,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: mediaGalleryListKeys({ httpRequestConfig: config }),
      });
    },
  });
}

/**
 * Gallery upload hook with polling support for processing items
 * Uses TanStack Query's built-in polling via refetchInterval
 */
export function useGalleryUpload(options?: {
  onUploadSuccess?: (item: { id: string; url: string | null; status: string }) => void;
  pollingStrategy?: 'list' | 'single';
  httpRequestConfig?: HttpServiceConfig;
}) {
  const queryClient = useQueryClient();
  const { httpRequestConfig: configFromStore } = useQueryConfig();
  const httpRequestConfig = options?.httpRequestConfig ?? configFromStore;
  const spaceId = httpRequestConfig?.space || '';

  // Track processing items for polling management
  const processingItemsRef = useRef<Set<string>>(new Set());
  const [hasProcessingItems, setHasProcessingItems] = useState(false);

  // Core mutations for the upload process
  const firebaseUrlMutation = useFirebaseStorageUrlMutation();
  const firebaseUploadMutation = useFirebaseUploadMutation();
  const createItemMutation = useCreateMediaGalleryItemMutation(httpRequestConfig);

  // Function to start monitoring an item for status changes
  const startMonitoring = useCallback((itemId: string) => {
    processingItemsRef.current.add(itemId);
    setHasProcessingItems(processingItemsRef.current.size > 0);

    if (options?.pollingStrategy === 'single') {
      // For single item polling, invalidate the specific item query to trigger polling
      queryClient.invalidateQueries({
        queryKey: mediaGalleryItemKeys({
          httpRequestConfig,
          params: {
            params: {
              id: itemId,
              spaceId,
            },
          },
        }),
      });
    } else {
      // For list polling (default), invalidate the list query to trigger polling
      queryClient.invalidateQueries({
        queryKey: mediaGalleryListKeys({ httpRequestConfig }),
      });
    }
  }, [queryClient, httpRequestConfig, spaceId, options?.pollingStrategy]);

  // Function to stop monitoring an item
  const stopMonitoring = useCallback((itemId: string) => {
    processingItemsRef.current.delete(itemId);
    setHasProcessingItems(processingItemsRef.current.size > 0);
  }, []);

  // Single upload mutation that handles the complete workflow
  const uploadMutation = useMutation({
    mutationFn: async ({
      file,
      onProgress,
    }: {
      file: File;
      onProgress?: (progress: number) => void;
    }) => {
      const uploadId = `${file.name}-${Date.now()}`;

      try {
        // For list polling, add to shared state
        if (options?.pollingStrategy === 'list') {
          addUpload({
            id: uploadId,
            file,
            progress: 0,
            status: 'waiting',
          });
        }

        // Start tracking upload time
        const startTime = performance.now();

        // Get Firebase Storage URL
        const { uploadUrl, storagePath } = await firebaseUrlMutation.mutateAsync({
          path: `${spaceId}/media/gallery`,
          fileName: file.name,
          targetEnv: httpRequestConfig.target,
        });

        // Update status to uploading
        if (options?.pollingStrategy === 'list') {
          updateUpload(uploadId, {
            status: 'uploading',
            progress: 0,
          });
        }

        // Upload to Firebase Storage
        const downloadUrl = await firebaseUploadMutation.mutateAsync({
          uploadUrl,
          file,
          onProgress: (progress: number) => {
            onProgress?.(progress);
            // Update shared state for list polling
            if (options?.pollingStrategy === 'list') {
              updateUpload(uploadId, {
                progress: Math.round(progress),
                status: 'uploading',
              });
            }
          },
          signal: new AbortController().signal,
          targetEnv: httpRequestConfig.target,
        });

        // Create media gallery item
        const response = await createItemMutation.mutateAsync({
          httpRequestConfig,
          params: {
            params: { spaceId },
            body: {
              origin: 'cms',
              originalStorageUrl: `gs://${storagePath}`,
              originalDownloadUrl: downloadUrl,
            },
          },
        });

        // Calculate upload duration and track
        const uploadDuration = performance.now() - startTime;
        trackUpload({
          mediaType: file.type.startsWith('image/') ? 'image' : 'video',
          location: 'gallery',
          source: 'computer',
          size: file.size,
          mimeType: file.type,
          space: spaceId,
          uploadDuration: Math.round(uploadDuration),
          hash: storagePath.split('/').pop() || 'unknown',
        });

        // If the item is in processing state, start monitoring it
        if (response.data && response.data.status === 'processing') {
          startMonitoring(response.data.id);
        }

        // Update shared state for list polling
        if (options?.pollingStrategy === 'list') {
          updateUpload(uploadId, {
            status: 'success',
            progress: 100,
          });
        }

        return response.data;
      } catch (error) {
        console.error('Upload error:', error);

        // Update shared state for list polling
        if (options?.pollingStrategy === 'list') {
          updateUpload(uploadId, {
            status: 'error',
            error: error instanceof Error ? error.message : 'Upload failed',
            progress: 0,
          });
        }

        throw error;
      }
    },
    onSuccess: (data) => {
      // Call the success callback if provided
      if (options?.onUploadSuccess && data) {
        options.onUploadSuccess({
          id: data.id,
          url: data.url,
          status: data.status,
        });
      }

      // Invalidate queries to refresh the gallery list
      queryClient.invalidateQueries({
        queryKey: mediaGalleryListKeys({ httpRequestConfig }),
      });
    },
  });

  return {
    uploadFile: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
    error: uploadMutation.error,
    reset: uploadMutation.reset,
    // Expose monitoring functions for external use
    startMonitoring,
    stopMonitoring,
    hasProcessingItems,
  };
}

/**
 * Hook to monitor a specific media gallery item's processing status
 * Uses TanStack Query's built-in polling and provides callbacks for status updates
 */
export function useMediaGalleryItemStatusMonitor(
  itemId: string | null,
  options?: {
    onStatusUpdate?: (data: { id: string; url: string | null; status: string }) => void;
    enabled?: boolean;
    httpRequestConfig?: HttpServiceConfig;
  },
) {
  const { httpRequestConfig: configFromStore } = useQueryConfig();
  const httpRequestConfig = options?.httpRequestConfig ?? configFromStore;
  const spaceId = httpRequestConfig?.space || '';

  const itemQuery = useMediaGalleryItemQuery({
    httpRequestConfig,
    params: {
      params: {
        id: itemId || '',
        spaceId,
      },
    },
  });

  // Call the status update callback when data changes - using useEffect to avoid setState during render
  const previousStatusRef = useRef<string | null>(null);

  useEffect(() => {
    if (itemQuery.data?.data !== undefined && itemQuery.data?.data !== null && options?.onStatusUpdate !== undefined) {
      const currentStatus = itemQuery.data.data.status;
      if (previousStatusRef.current !== currentStatus) {
        previousStatusRef.current = currentStatus;
        options.onStatusUpdate({
          id: itemQuery.data.data.id,
          url: itemQuery.data.data.url,
          status: currentStatus,
        });
      }
    }
  }, [itemQuery.data?.data, options]);

  return {
    data: itemQuery.data?.data,
    isLoading: itemQuery.isLoading,
    error: itemQuery.error,
    refetch: itemQuery.refetch,
  };
}
